import React, { useState, useEffect } from 'react';
import NonSkCountryStep from './NonSkCountryStep';
import NonSkVerifyStep from './NonSkVerifyStep';
import { config } from '../../config';
import { trackEvent } from '../../utils/posthog';

interface NonSkReceiptFlowProps {
  onSuccess: (receiptData: any) => void;
  onCancel: () => void;
  authenticatedFetch: (url: string, options?: RequestInit) => Promise<Response>;
  showError: (message: string) => void;
}

const NonSkReceiptFlow: React.FC<NonSkReceiptFlowProps> = ({
  onSuccess,
  onCancel,
  authenticatedFetch,
  showError
}) => {
  const [nonSkStep, setNonSkStep] = useState<'country' | 'verify' | 'success'>('country');
  const [countries, setCountries] = useState<any[]>([]);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [nonSkReceiptData, setNonSkReceiptData] = useState<any>(null);
  const [nonSkFile, setNonSkFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');

  // Load countries on mount
  useEffect(() => {
    if (countries.length === 0 && !countriesLoading) {
      setCountriesLoading(true);
      authenticatedFetch(`${config.apiUrl}/getCountries`)
        .then(response => response.json())
        .then(data => {
          if (data.success) {
            setCountries(data.countries);
          }
        })
        .catch(error => {
          // Fallback countries if API fails
          setCountries([
            { country_code: 'CZ', country_name: 'Czech Republic' },
            { country_code: 'PL', country_name: 'Poland' },
            { country_code: 'HU', country_name: 'Hungary' },
            { country_code: 'AT', country_name: 'Austria' },
            { country_code: 'DE', country_name: 'Germany' }
          ]);
        })
        .finally(() => {
          setCountriesLoading(false);
        });
    }
  }, [countries.length, countriesLoading, authenticatedFetch]);

  // Handle file change
  const handleFileChange = (file: File | null) => {
    setNonSkFile(file);
  };

  // Handle country selection + file upload submission
  const handleCountryStepSubmit = async (country: string, file: File) => {
    if (!country) {
      showError('Vyberte krajinu pôvodu bločka');
      return;
    }

    if (!file) {
      showError('Vyberte fotografiu bločka');
      return;
    }

    setIsProcessing(true);
    setStatusMessage('🔍 OpenAI analyzuje bloček... Môže to trvať 1-2 minúty.');

    try {
      // Upload and start analysis (returns job ID immediately)
      const formData = new FormData();
      formData.append('file', file);
      formData.append('country_code', country);
      formData.append('action', 'store');

      const response = await authenticatedFetch(`${config.apiUrl}/analyzeNonSkReceipt`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        let errorMsg = 'Neznáma chyba';
        try {
          const errorData = await response.json();
          errorMsg = errorData.message || `HTTP ${response.status}`;
        } catch {
          errorMsg = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMsg);
      }

      const startResult = await response.json();

      if (!startResult || !startResult.jobId) {
        throw new Error('Neplatná odpoveď zo servera - chýba job ID');
      }

      const jobId = startResult.jobId;
      setStatusMessage('⏳ Spracúvam bloček... Čakám na výsledok.');

      // Poll for job completion
      let attempts = 0;
      const maxAttempts = 60; // 3 minutes max (60 * 3 seconds)

      while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 3000)); // Wait 3 seconds
        attempts++;

        const statusResponse = await authenticatedFetch(`${config.apiUrl}/getAnalysisStatus?jobId=${jobId}`);

        if (!statusResponse.ok) {
          throw new Error(`Chyba pri kontrole stavu: HTTP ${statusResponse.status}`);
        }

        const statusResult = await statusResponse.json();

        if (statusResult.status === 'completed') {
          // Job completed successfully
          if (statusResult.result && statusResult.result.receiptData) {
            setNonSkReceiptData(statusResult.result.receiptData);
            setNonSkStep('verify');
            setStatusMessage('');
            return;
          } else {
            throw new Error('Výsledok neobsahuje dáta bločka');
          }
        } else if (statusResult.status === 'failed') {
          // Job failed
          throw new Error(statusResult.error || 'Analýza zlyhala');
        }

        // Update progress message
        const elapsed = attempts * 3;
        setStatusMessage(`⏳ Spracúvam bloček... (${elapsed}s)`);
      }

      // Timeout after maxAttempts
      throw new Error('Časový limit prekročený - analýza trvala príliš dlho');

    } catch (error: any) {
      console.error('Error analyzing non-SK receipt:', error);
      const errorMessage = error?.message || String(error);

      // Track upload failure (non-SK receipt)
      trackEvent('receipt_upload_failed', {
        input_method: 'photo_upload',
        source: country || 'non_sk',
        error_message: errorMessage,
        workflow: 'non_sk'
      });

      showError(`Chyba pri analýze: ${errorMessage}`);
    } finally {
      setIsProcessing(false);
      setStatusMessage('');
    }
  };

  // Handle verification submission
  const handleVerifySubmit = async (formData: any) => {
    setIsProcessing(true);
    setStatusMessage('Ukladám neslovenský bloček...');
    try {
      // Store the original receipt data before making the API call
      const originalReceiptData = nonSkReceiptData;

      const response = await authenticatedFetch(`${config.apiUrl}/storeNonSkReceipt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to store receipt');
      }
      const result = await response.json();

      // Combine storage result with original receipt data
      const combinedData = {
        ...originalReceiptData,
        ...result,
        // Keep the original receipt structure for the success page
        receiptData: originalReceiptData.receiptData || { receipt: originalReceiptData.receipt }
      };

      // Transform to success page format
      const receipt = combinedData.receiptData?.receipt || combinedData.receipt;
      const successData = {
        receipt_id: combinedData.receiptId,
        receipt_hash: combinedData.receiptId,
        merchant: receipt?.organization?.name || 'Neznámy obchod',
        addr_line: receipt?.organization?.streetName || '',
        addr_city: receipt?.organization?.municipality || '',
        addr_postcode: receipt?.organization?.postalCode || '',
        addr_country: selectedCountry || combinedData.countryCode || '',
        total_gross: receipt?.totalPrice || 0,
        datetime: receipt?.issueDate || new Date().toISOString(),
        source: 'NONSK'
      };

      // Track successful receipt upload (Non-SK receipt)
      trackEvent('receipt_uploaded', {
        source: selectedCountry || combinedData.countryCode || 'non_sk',
        input_method: 'photo_upload',
        merchant: successData.merchant,
        total_amount: successData.total_gross,
        currency: combinedData.receipt?.currency || 'EUR'
      });

      // Call onSuccess to show success page in parent
      onSuccess(successData);

    } catch (error) {
      console.error('Error storing non-SK receipt:', error);
      showError(`Chyba pri ukladaní: ${error}`);
    } finally {
      setIsProcessing(false);
      setStatusMessage('');
    }
  };

  // Render country selection step
  if (nonSkStep === 'country') {
    return (
      <NonSkCountryStep
        countries={countries}
        countriesLoading={countriesLoading}
        selectedCountry={selectedCountry}
        onCountryChange={setSelectedCountry}
        file={nonSkFile}
        onFileChange={handleFileChange}
        isProcessing={isProcessing}
        statusMessage={statusMessage}
        onSubmit={handleCountryStepSubmit}
        onCancel={onCancel}
      />
    );
  }

  // Render verification step
  if (nonSkStep === 'verify' && nonSkReceiptData) {
    return (
      <NonSkVerifyStep
        receiptData={nonSkReceiptData}
        selectedCountry={selectedCountry}
        onReceiptDataChange={setNonSkReceiptData}
        isProcessing={isProcessing}
        onSubmit={handleVerifySubmit}
        onBack={() => setNonSkStep('country')}
      />
    );
  }

  return null;
};

export default NonSkReceiptFlow;
